import { v4 as uuidv4 } from 'uuid';
import { parseHtmlFragment } from '@shared/helpers/html.helper';
import { COMMENTS_HTML_ATTRIBUTES } from '../../../comments/constants';
import { DOCUMENT_HTML_ATTRIBUTES } from '../../constants';
import { SECTION_HTML_SELECTOR } from '../../tiptap/nodes/section/constants';

/** One object shared by every element of a section, so "same section" is a pointer comparison. */
export type TSection = { id: string | null; openTag: string };

export type TElement = {
  id: string | null;
  html: string;
  section?: TSection;
};

const SECTION_CLOSING_TAG = '</section>';

const sectionChildren = (element: Element): TElement[] => {
  // Measured off the end, not up to the first `>`: an attribute value may legally contain one.
  const { outerHTML, innerHTML } = element;
  const openTag = outerHTML.slice(0, outerHTML.length - innerHTML.length - SECTION_CLOSING_TAG.length);
  const section: TSection = { id: element.getAttribute(DOCUMENT_HTML_ATTRIBUTES.CUSTOM_UNIQUE_ID), openTag };
  return Array.from(element.children).map((child) => ({
    id: child.getAttribute(DOCUMENT_HTML_ATTRIBUTES.CUSTOM_UNIQUE_ID),
    html: child.outerHTML,
    section,
  }));
};

// `children` not `childNodes`: ProseMirror serializes with no whitespace between top-level nodes.
export const splitElements = (html: string): TElement[] =>
  Array.from(parseHtmlFragment(html).children).flatMap((element) =>
    // An empty section has no children to stand in for it, so it stays one element and keeps its place.
    element.matches(SECTION_HTML_SELECTOR) && element.children.length > 0
      ? sectionChildren(element)
      : [{ id: element.getAttribute(DOCUMENT_HTML_ATTRIBUTES.CUSTOM_UNIQUE_ID), html: element.outerHTML }]
  );

export const concatElementHtml = (elements: readonly TElement[]): string =>
  elements.map((element) => element.html).join('');

export const joinElements = (elements: readonly TElement[]): string => {
  const parts: string[] = [];
  let open: TSection | undefined;
  elements.forEach((element) => {
    if (element.section !== open) {
      if (open) parts.push('</section>');
      if (element.section) parts.push(element.section.openTag);
      open = element.section;
    }
    parts.push(element.html);
  });
  if (open) parts.push('</section>');
  return parts.join('');
};

export const countSections = (html: string): number =>
  new Set(
    splitElements(html)
      .map((element) => element.section)
      .filter(Boolean)
  ).size;

/** Every section wrapper gone, and every element that was inside one kept where it was. */
export const withoutSections = (html: string): string => concatElementHtml(splitElements(html));

/**
 * An element with one comment's highlight taken out, as the elements it becomes.
 *
 * A comment is stored two ways: a `selection-mark` inside a paragraph, and a `selection-node` around
 * whole blocks. Unwrapping the second one turns a single element into its children, which is why this
 * returns a list rather than one element - the caller has to expect the count to change.
 */
export const withoutComment = (element: TElement, commentId: string): TElement[] => {
  const fragment = parseHtmlFragment(element.html);
  Array.from(fragment.querySelectorAll(`[${COMMENTS_HTML_ATTRIBUTES.COMMENT_ID}]`))
    .filter((marked) => marked.getAttribute(COMMENTS_HTML_ATTRIBUTES.COMMENT_ID) === commentId)
    .forEach((marked) => marked.replaceWith(...Array.from(marked.childNodes)));
  return Array.from(fragment.children).map((child) => ({
    id: child.getAttribute(DOCUMENT_HTML_ATTRIBUTES.CUSTOM_UNIQUE_ID),
    html: child.outerHTML,
    ...(element.section ? { section: element.section } : {}),
  }));
};

const OPENING_TAG_PATTERN = /<[a-z][a-z0-9-]*[\s>]/gi;

// A commit builds new objects only for what changed, so an unchanged element keeps its count.
const counted = new WeakMap<TElement, number>();
export const nodeCount = (element: TElement): number => {
  const known = counted.get(element);
  if (known !== undefined) return known;
  const count = (element.html.match(OPENING_TAG_PATTERN) ?? []).length;
  counted.set(element, count);
  return count;
};

export const indexOfId = (elements: readonly TElement[], id: string): number =>
  elements.findIndex((element) => element.id === id);

// Leading whitespace so `data-loaded-id`, `data-heading-id` and `data-target-id` do not match - they name ids in another document.
const OWN_ID_PATTERN = /\s(?:id|data-id|data-comment-id|data-field-id)="([^"]+)"/g;

export const buildContainedIdIndex = (elements: readonly TElement[]): Map<string, number> => {
  const index = new Map<string, number>();
  elements.forEach((element, elementIndex) => {
    for (const match of element.html.matchAll(OWN_ID_PATTERN)) {
      // First wins: an id should be unique, and the earlier element is the one that renders it.
      if (!index.has(match[1])) index.set(match[1], elementIndex);
    }
  });
  return index;
};

const TITLE_TAG_PATTERN = /^<title-node[\s>]/i;

export const isTitleElement = (element: TElement): boolean => TITLE_TAG_PATTERN.test(element.html);

/**
 * Every document starts with a title element, empty or not. The docx importer omits it when the source
 * has no paragraph styled "Title", so it is added here rather than left to ProseMirror to synthesize
 * - which it could only do for the part that happens to be loaded.
 */
export const withTitleElement = (elements: readonly TElement[]): TElement[] => {
  if (elements.length > 0 && isTitleElement(elements[0])) return [...elements];
  // A save without an edit never runs the editor's id pass, so the id is stamped here instead.
  const id = uuidv4();
  return [{ id, html: `<title-node ${DOCUMENT_HTML_ATTRIBUTES.CUSTOM_UNIQUE_ID}="${id}"></title-node>` }, ...elements];
};

export const titleTextOf = (elements: readonly TElement[]): string => {
  if (!elements.length || !isTitleElement(elements[0])) return '';
  return (parseHtmlFragment(elements[0].html).textContent ?? '').trim();
};
